/**
 * Culians – Product Page JS
 *
 * Features:
 *  1. FIVE-STATE CTA BUTTON.
 *     Manages the Add to Cart button across five states:
 *       a. "SELECT SIZE"       – default before any size is selected  (fill #0E0E0E)
 *       b. "ADD TO BAG"        – after selecting an in-stock size     (fill #000000)
 *       c. "SOLD OUT"          – selected variant is unavailable      (fill #0E0E0E, faded)
 *       d. "JOIN THE WAITLIST" – when ALL sizes are out of stock      (fill #7A7A7A)
 *       e. "PRE-ORDER"         – when the selected variant is pre-orderable (fill #000000)
 *
 *     All button text is ALL CAPS.
 *     A MutationObserver locks the button text so Atelier's success
 *     animation / "Add to cart" reset can never overwrite our label.
 *
 *     Detection logic:
 *       • Waitlist: every non-swatch size radio has data-option-available="false".
 *       • Pre-order: selected variant's inventory_policy === 'continue'.
 *       • Sold out: selected variant's available === false AND inventory_policy !== 'continue'.
 *       • Variant metadata is fetched once from /products/{handle}.js.
 *
 *     Data attributes set on the DOM for CSS hooks:
 *       form[data-culians-size-state="select|add|soldout|waitlist|preorder"]
 *       [data-testid="product-information"][data-culians-product-mode="waitlist|normal"]
 *
 *  2. PAGINATION DOTS.
 *     Injects a fixed-position vertical column of dots (bottom-left of viewport)
 *     that tracks which product image is currently in the viewport centre.
 *     Uses IntersectionObserver so no scroll events are needed.
 *     Desktop only (mobile uses Atelier's built-in slideshow dots via CSS display:none).
 *
 * Dependencies: none (vanilla JS, no framework)
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
     1. FIVE-STATE CTA BUTTON
  ───────────────────────────────────────────────────────────────────────── */

  var TEXT_SELECT   = 'SELECT A SIZE';
  var TEXT_ADD      = 'ADD TO BAG';
  var TEXT_ADDING   = 'ADDING';
  var TEXT_ADDED    = 'ADDED';
  var TEXT_SOLDOUT  = 'SOLD OUT';
  var TEXT_WAITLIST = 'JOIN WAITLIST';
  var TEXT_PREORDER = 'PRE-ORDER';
  var TEXT_SUCCESS  = 'Item added to your Shopping Bag';
  var TEXT_MAX_QTY  = 'The maximum quantity of this item is already in your bag';

  /**
   * Cache of variant metadata fetched from the product .js endpoint.
   * Keys are variant IDs (strings); values are { available, inventoryPolicy }.
   */
  var variantCache = {};

  /**
   * Tracks the current CTA state so the MutationObserver can restore
   * the correct text if Atelier tries to overwrite it.
   */
  var currentState = 'select';

  /**
   * Guard flag: prevents the MutationObserver from looping when we
   * ourselves set the textContent.
   */
  var isApplyingText = false;

  /**
   * Guard flag: ensures the 'variant:update' document listener is added only once,
   * even if init() or shopify:section:load fires multiple times.
   */
  var variantUpdateListenerAttached = false;

  /**
   * Tracks whether the user has explicitly clicked a size option.
   * Until this is true we keep the button in the 'select' state and
   * do not let Atelier's auto-selected variant override us.
   */
  var userHasSelectedSize = false;

  /**
   * The state that was active before we entered 'adding'.
   * Used to restore the button after ADDED (either 'add' or 'preorder').
   */
  var stateBeforeAdding = 'add';

  /**
   * Safety timeout ID: restores state if cart events never fire (network failure).
   */
  var addingTimeoutId = null;

  /**
   * Monotonic counter incremented on every setAtcState call.
   * Each fade closure captures its own snapshot; if it no longer matches
   * atcFadeId when the timeout fires, the fade is stale and silently aborts.
   */
  var atcFadeId = 0;

  /**
   * True while a fade-out/fade-in transition is in progress.
   * The MutationObserver guard checks this to avoid interrupting mid-fade.
   */
  var isAtcFading = false;

  /* ── DOM helpers ─────────────────────────────────────────────────────── */

  /**
   * Returns the text <span> inside the ATC button that contains the label
   * ("Add to cart", "Add to bag", etc.).
   *
   * Tries selectors in order of specificity. Atelier's exact HTML varies
   * across theme versions; the fallback scan finds the first leaf <span>
   * whose text looks like an ATC label.
   *
   * Scoped to the main product form only (not the sticky bar).
   */
  function getAtcTextSpan() {
    var section = document.querySelector('[data-testid="product-information"]');
    if (!section) return null;

    // Known Atelier selector patterns, most specific first
    var selectors = [
      '.add-to-cart-button .add-to-cart-text__content > span:first-child',
      '.add-to-cart-button .add-to-cart-text__label',
      '.add-to-cart-button .add-to-cart-text > span:first-child',
      '.add-to-cart-button .btn__text'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var el = section.querySelector(selectors[i]);
      if (el) return el;
    }

    // Fallback: walk every <span> inside the button, find the one whose
    // text content looks like an ATC label (contains "cart", "bag", "size", etc.)
    var btn = section.querySelector('.add-to-cart-button');
    if (!btn) return null;
    var spans = btn.querySelectorAll('span');
    var cartKeywords = /cart|bag|size|waitlist|order|sold/i;
    for (var j = 0; j < spans.length; j++) {
      var sp = spans[j];
      // Leaf span (no child spans) with matching text
      if (!sp.querySelector('span') && cartKeywords.test(sp.textContent)) {
        return sp;
      }
    }
    // Last resort: first span with any text
    for (var k = 0; k < spans.length; k++) {
      if (spans[k].textContent.trim() && !spans[k].querySelector('span')) {
        return spans[k];
      }
    }
    return null;
  }

  /**
   * Returns all radio inputs inside non-swatch button fieldsets
   * (i.e. the Size option, not the Color swatch option).
   */
  function getSizeInputs() {
    return document.querySelectorAll(
      '[data-testid="product-information"] .variant-option--buttons:not(.variant-option--swatches) input[type="radio"]'
    );
  }

  /**
   * Returns true if the product has at least one non-swatch variant option (size).
   */
  function hasSizeOption() {
    return getSizeInputs().length > 0;
  }

  function getProductForm() {
    return document.querySelector(
      '[data-testid="product-information"] form[action*="/cart/add"]'
    );
  }

  function getSection() {
    return document.querySelector('[data-testid="product-information"]');
  }

  /**
   * Reads the hidden variant-ID input inside the cart form.
   * Atelier updates this value whenever the selected variant changes.
   */
  function getSelectedVariantId() {
    var form = getProductForm();
    if (!form) return null;
    var input = form.querySelector('input[name="id"]');
    return input ? String(input.value) : null;
  }

  /**
   * Parses the variant JSON embedded inside the <variant-picker> element.
   * This is the server-rendered selected variant and includes inventory_policy.
   */
  function getEmbeddedVariantJson() {
    var picker = document.querySelector(
      '[data-testid="product-information"] variant-picker'
    );
    if (!picker) return null;
    var script = picker.querySelector('script[type="application/json"]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }

  /* ── Mode detection ──────────────────────────────────────────────────── */

  /**
   * Returns true when EVERY size option is unavailable → waitlist mode.
   */
  function isAllSizesUnavailable() {
    var inputs = getSizeInputs();
    if (inputs.length === 0) return false;
    return Array.prototype.every.call(inputs, function (input) {
      return input.getAttribute('data-option-available') === 'false';
    });
  }

  /**
   * Checks whether a given variant ID corresponds to a pre-orderable variant.
   * A variant is pre-orderable when available === true AND inventory_policy === 'continue'.
   */
  function isPreorderVariant(vid) {
    if (vid && variantCache[vid]) {
      var v = variantCache[vid];
      if (v.available && v.inventoryPolicy === 'continue') return true;
    }
    // Fallback: embedded JSON for the initially selected variant
    var json = getEmbeddedVariantJson();
    if (json && String(json.id) === String(vid)) {
      if (json.available && json.inventory_policy === 'continue') return true;
    }
    return false;
  }

  /**
   * Checks whether a given variant ID corresponds to a sold-out variant.
   * Sold out: available === false AND inventory_policy !== 'continue'.
   */
  function isSoldOutVariant(vid) {
    if (vid && variantCache[vid]) {
      var v = variantCache[vid];
      if (!v.available && v.inventoryPolicy !== 'continue') return true;
    }
    // Fallback: embedded JSON
    var json = getEmbeddedVariantJson();
    if (json && String(json.id) === String(vid)) {
      if (!json.available && json.inventory_policy !== 'continue') return true;
    }
    return false;
  }

  /**
   * Returns the button label string for a given state key.
   */
  function getTextForState(state) {
    var labels = {
      select:   TEXT_SELECT,
      add:      TEXT_ADD,
      adding:   TEXT_ADDING,
      added:    TEXT_ADDED,
      soldout:  TEXT_SOLDOUT,
      waitlist: TEXT_WAITLIST,
      preorder: TEXT_PREORDER
    };
    return labels[state] || TEXT_SELECT;
  }

  /**
   * Determines the appropriate CTA state after a size is selected.
   * @returns {'select'|'add'|'soldout'|'waitlist'|'preorder'}
   */
  function determineCTAState() {
    if (isAllSizesUnavailable()) return 'waitlist';

    var vid = getSelectedVariantId();
    if (!vid) return 'select';
    if (isPreorderVariant(vid)) return 'preorder';
    if (isSoldOutVariant(vid)) return 'waitlist';

    return 'add';
  }

  /* ── State application ───────────────────────────────────────────────── */

  /**
   * Writes the expected label text directly to the ATC span.
   * Extracted so it can be called both synchronously and after delays.
   */
  function applyStateText() {
    var span = getAtcTextSpan();
    if (span) {
      isApplyingText = true;
      span.textContent = getTextForState(currentState);
      isApplyingText = false;
    }
  }

  /**
   * Updates the ATC button label and sets data attributes for CSS hooks.
   *
   * Applies the text immediately, then again after 100 ms and 400 ms.
   * The delayed passes catch cases where Atelier's web component
   * re-stamps the button DOM synchronously after our first write
   * (e.g. during variant-picker initialisation).
   *
   * @param {'select'|'add'|'soldout'|'waitlist'|'preorder'} state
   */
  function setState(state) {
    currentState = state;

    applyStateText();
    setTimeout(applyStateText, 100);
    setTimeout(applyStateText, 400);
    setTimeout(applyStateText, 1500);

    // Drive button-fill colour via CSS
    var form = getProductForm();
    if (form) {
      form.setAttribute('data-culians-size-state', state);
    }

    // Drive size-option styling via CSS
    var section = getSection();
    if (section) {
      section.setAttribute(
        'data-culians-product-mode',
        isAllSizesUnavailable() ? 'waitlist' : 'normal'
      );

      // When state is waitlist, the button must be clickable even though
      // Atelier disables it for sold-out variants — otherwise the
      // JOIN WAITLIST click never fires and the drawer can't open.
      if (state === 'waitlist') {
        var btn = section.querySelector('.add-to-cart-button');
        if (btn) btn.removeAttribute('disabled');
      }

      // Notify any listeners (e.g. the inline waitlist) of the state
      // change so they can reset their own UI when the customer picks
      // a different (in-stock) size.
      section.dispatchEvent(new CustomEvent('culians:state-change', {
        detail: { state: state }
      }));
    }
  }

  /* ── Variant data fetch ──────────────────────────────────────────────── */

  /**
   * Fetches the product JSON from Shopify's /products/{handle}.js endpoint
   * and populates variantCache with each variant's availability metadata.
   *
   * @param {Function} callback – called when fetch completes (success or error)
   */
  function fetchVariantData(callback) {
    var picker = document.querySelector(
      '[data-testid="product-information"] variant-picker'
    );
    var productUrl = picker ? picker.getAttribute('data-product-url') : null;

    if (!productUrl) {
      callback();
      return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('GET', productUrl + '.js', true);
    xhr.onload = function () {
      if (xhr.status === 200) {
        try {
          var product = JSON.parse(xhr.responseText);
          (product.variants || []).forEach(function (v) {
            variantCache[String(v.id)] = {
              available: v.available,
              inventoryPolicy: v.inventory_policy || 'deny'
            };
          });
        } catch (e) { /* silent – pre-order detection falls back to embedded JSON */ }
      }
      callback();
    };
    xhr.onerror = function () { callback(); };
    xhr.send();
  }

  /* ── Color label: "Color : Navy" exact format ───────────────────────── */

  /**
   * Normalises the swatch legend to render as "Color : Navy" with no extra spaces.
   *
   * Root cause: Atelier renders the legend text node as "Color " (trailing space).
   * Our CSS ::before on .variant-option__swatch-value injects " : ", producing
   * "Color  : Navy" (double space). Trimming the text node fixes this permanently.
   *
   * Called on load and after color variant changes.
   */
  function fixColorLabel() {
    var legends = document.querySelectorAll(
      '[data-testid="product-information"] .variant-option--swatches legend'
    );
    Array.prototype.forEach.call(legends, function (legend) {
      // Trim every text node in the legend (removes trailing/leading spaces)
      Array.prototype.forEach.call(legend.childNodes, function (node) {
        if (node.nodeType === Node.TEXT_NODE) {
          var trimmed = node.textContent.trim();
          if (node.textContent !== trimmed) {
            node.textContent = trimmed;
          }
        }
      });
    });
  }

  /* ── Text stability: MutationObserver ───────────────────────────────── */

  /**
   * Watches the ATC button container for any DOM mutations.
   * Observes the add-to-cart-component (or button) rather than a cached span,
   * so Atelier's web component re-renders (which replace the inner span node)
   * do not silently disconnect the observer.
   * Re-calls getAtcTextSpan() on every callback to always target the live node.
   *
   * The isApplyingText guard prevents the observer from looping
   * on our own textContent writes.
   */
  function watchAtcText() {
    var section = getSection();
    if (!section) return;

    var container = section.querySelector('add-to-cart-component') ||
                    section.querySelector('.add-to-cart-button');
    if (!container) return;

    var observer = new MutationObserver(function () {
      if (isApplyingText || isAtcFading) return;
      var span = getAtcTextSpan();
      if (!span) return;
      var expected = getTextForState(currentState);
      if (span.textContent !== expected) {
        isApplyingText = true;
        span.textContent = expected;
        isApplyingText = false;
      }
    });

    observer.observe(container, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  /* ── Event listeners ─────────────────────────────────────────────────── */

  /**
   * Attaches a pointerdown listener on the section that sets
   * data-culians-select-click on the form while the SELECT A SIZE button is
   * pressed. Removed on pointerup/pointercancel. CSS :active is suppressed
   * by browsers on [disabled] elements even when pointer-events:auto is set.
   */
  function attachSelectClickFlash() {
    var section = getSection();
    if (!section) return;

    section.addEventListener('pointerdown', function (e) {
      if (!e.target.closest('.add-to-cart-button')) return;
      var form = getProductForm();
      if (!form || form.getAttribute('data-culians-size-state') !== 'select') return;

      form.setAttribute('data-culians-select-click', '');

      function cleanup() {
        form.removeAttribute('data-culians-select-click');
        document.removeEventListener('pointerup', cleanup);
        document.removeEventListener('pointercancel', cleanup);
      }
      document.addEventListener('pointerup', cleanup);
      document.addEventListener('pointercancel', cleanup);
    });
  }

  /**
   * Attaches change listeners to the variant picker inside the product section.
   * When a non-swatch (size) radio is clicked, determines the new CTA state.
   */
  function attachSizeListeners() {
    var section = getSection();
    if (!section) return;

    // The browser fires no 'change' event when the user clicks a radio that is
    // already checked. In SELECT A SIZE state the server pre-checks the first
    // available size, so clicking that size first produces no 'change' and the
    // selection is silently ignored. This pointerdown listener catches that case:
    // at pointerdown the radio is still in its pre-click state, so input.checked
    // being true means the click will NOT fire 'change' — we handle it ourselves.
    section.addEventListener('pointerdown', function (e) {
      var input = e.target.closest(
        '.variant-option--buttons:not(.variant-option--swatches) input[type="radio"]'
      );
      if (!input) {
        var label = e.target.closest(
          '.variant-option--buttons:not(.variant-option--swatches) .variant-option__button-label'
        );
        if (label && !label.closest('.variant-option__button-label--has-swatch')) {
          input = label.querySelector('input[type="radio"]');
        }
      }
      if (!input || !input.checked) return; // unchecked → 'change' will fire normally
      if (input.closest('.variant-option__button-label--has-swatch')) return; // swatch
      if (currentState !== 'select') return; // only needed from SELECT A SIZE state

      userHasSelectedSize = true;
      requestAnimationFrame(function () {
        if (isAllSizesUnavailable()) {
          setState('waitlist');
        } else {
          var isAvailable = input.getAttribute('data-option-available') !== 'false';
          setState(isAvailable ? 'add' : 'waitlist');
        }
        // Do NOT set data-culians-transitioning here — this handler runs when
        // the user clicks an already-checked radio, so no variant change is
        // occurring and no variant:update event will fire to clear the
        // transitioning attribute. Setting it would leave the button stuck
        // at the grey #333 transition fill forever, even though state is now 'add'.
      });
    });

    section.addEventListener('change', function (e) {
      var input = e.target;
      if (input.type === 'radio' && input.closest('.variant-option--buttons')) {
        if (!input.closest('.variant-option__button-label--has-swatch')) {
          // Mark that the user has explicitly chosen a size.
          userHasSelectedSize = true;
          // Size radio changed — set the immediate transition state using the clicked
          // radio's data-option-available attribute rather than determineCTAState().
          //
          // determineCTAState() reads the hidden input[name="id"] to get the variant
          // ID, but that input is only updated by Atelier's #onVariantUpdate AFTER the
          // section fetch completes. During the RAF the input still holds the previous
          // variant ID, so for an OOS→in-stock switch isSoldOutVariant() returns true
          // (old OOS id) and the state incorrectly stays 'waitlist' through the whole
          // fetch window — blocking the #333333 transition-fill CSS rule which targets
          // form[data-culians-size-state="add"].
          //
          // The authoritative final state is always set by the variant:update listener
          // after the fetch, so this RAF only needs to be correct for the transition.
          requestAnimationFrame(function () {
            if (isAllSizesUnavailable()) {
              setState('waitlist');
            } else {
              var isAvailable = input.getAttribute('data-option-available') !== 'false';
              setState(isAvailable ? 'add' : 'waitlist');
            }
            // Mark the form as transitioning so CSS can apply the hover-fill (#333333)
            // regardless of which state (add or waitlist) the transition targets.
            // Cleared by the variant:update listener once the final state is confirmed.
            var form = getProductForm();
            if (form) form.setAttribute('data-culians-transitioning', '');
          });
        } else {
          // Color / swatch radio changed — re-normalise the "Color : [Name]" label
          requestAnimationFrame(fixColorLabel);
        }
      }
    });
  }

  /**
   * Attaches a document-level 'variant:update' listener to re-apply our button
   * text AFTER Atelier's ProductFormComponent.#onVariantUpdate has morphed the
   * ATC button with server-rendered HTML ("Sold out" / "Add to cart").
   *
   * Event bubble order for a variant change:
   *   variant-picker (dispatch) → … → .shopify-section
   *     └─ ProductFormComponent.#onVariantUpdate runs here:
   *          - updates the hidden variant ID input
   *          - calls morph(currentAddToCartButton, newAddToCartButton)
   *        → document (our listener runs here, after the morph)
   *
   * Because this fires post-morph, getSelectedVariantId() already returns the
   * new variant ID and the embedded JSON has been updated by updateVariantPicker(),
   * so determineCTAState() gives correct results even before variantCache is warm.
   *
   * Guard flag prevents duplicate listeners across init / shopify:section:load.
   */
  function attachVariantUpdateListener() {
    if (variantUpdateListenerAttached) return;
    variantUpdateListenerAttached = true;

    document.addEventListener('variant:update', function () {
      if (!hasSizeOption()) return;
      // Do not override the 'select' state with Atelier's auto-selected variant
      // on page load — only update after the user has explicitly chosen a size.
      if (!userHasSelectedSize) return;
      // requestAnimationFrame ensures any synchronous DOM writes from
      // #onVariantUpdate (enable/disable, attribute updates) have settled.
      requestAnimationFrame(function () {
        setState(determineCTAState());
        // Transition is complete — remove the transitioning flag so the CSS
        // transition-fill rule no longer overrides the resting button colour.
        var form = getProductForm();
        if (form) form.removeAttribute('data-culians-transitioning');
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     2. PAGINATION DOTS
  ───────────────────────────────────────────────────────────────────────── */

  var dotsContainer     = null;
  var dotsObserver      = null;
  var dotsScrollHandler = null;
  var dotsResizeHandler = null;

  /**
   * Returns the .media-gallery element (left column image stack).
   */
  function getMediaSection() {
    return document.querySelector('[data-testid="product-information"] .media-gallery');
  }

  /**
   * Shows/hides the dot strip and positions it 24px above the visible bottom
   * of the media stack.
   *
   * Position logic:
   *   • When the media bottom is inside the viewport, bottom = (vh - mediaBottom + 24).
   *     This keeps the dots exactly 24px above the last image's lower edge.
   *   • When the media extends below the viewport (user is mid-scroll), the
   *     computed value would go negative — clamp to 24px (viewport bottom floor).
   *   • Dots fade out as soon as the media is fully out of view (top OR bottom).
   */
  function updateDotsVisibility() {
    if (!dotsContainer) return;
    var media = getMediaSection();
    if (!media) {
      dotsContainer.style.opacity = '0';
      return;
    }
    var rect = media.getBoundingClientRect();
    // Dots are visible while any part of the media stack is in the viewport.
    // CSS keeps them fixed at bottom: 24px; left: 24px — no JS position override.
    var visible = rect.bottom > 24 && rect.top < window.innerHeight - 24;
    dotsContainer.style.opacity = visible ? '1' : '0';
    dotsContainer.style.pointerEvents = 'none';
  }

  /**
   * Removes the dots container, disconnects the observer, and clears
   * all scroll/resize listeners created by initPaginationDots.
   */
  function destroyDots() {
    if (dotsObserver) { dotsObserver.disconnect(); dotsObserver = null; }
    if (dotsScrollHandler) {
      window.removeEventListener('scroll', dotsScrollHandler);
      dotsScrollHandler = null;
    }
    if (dotsResizeHandler) {
      window.removeEventListener('resize', dotsResizeHandler);
      dotsResizeHandler = null;
    }
    if (dotsContainer && dotsContainer.parentNode) {
      dotsContainer.parentNode.removeChild(dotsContainer);
      dotsContainer = null;
    }
  }

  /**
   * Creates the pagination dot UI with accurate center-distance syncing.
   *
   * On each IntersectionObserver callback (fired at multiple thresholds as
   * slides scroll), we iterate every slide, measure its center distance from
   * the viewport center, and mark the closest visible slide as active.
   * This avoids the "first-to-intersect wins" problem of rootMargin-only logic.
   *
   * Dots are hidden via opacity when the media stack is off-screen, so they
   * never appear over recommendations or the footer.
   */
  function initPaginationDots() {
    destroyDots();

    var mediaGrid = document.querySelector(
      '[data-testid="product-information"] .media-gallery__grid'
    );
    if (!mediaGrid) return;

    var slides = Array.prototype.slice.call(mediaGrid.querySelectorAll(':scope > li'));
    if (slides.length <= 1) return;

    // Build dots container
    dotsContainer = document.createElement('div');
    dotsContainer.id = 'culians-pagination-dots';
    dotsContainer.setAttribute('aria-hidden', 'true');
    dotsContainer.style.opacity = '0'; // start hidden; updateDotsVisibility reveals it

    var dots = slides.map(function (_, i) {
      var dot = document.createElement('span');
      dot.className = 'culians-dot';
      dot.setAttribute('data-index', String(i));
      dotsContainer.appendChild(dot);
      return dot;
    });

    document.body.appendChild(dotsContainer);
    if (dots[0]) dots[0].classList.add('is-active');

    var activeIndex = 0;

    function setActive(index) {
      if (index === activeIndex && dots[index] &&
          dots[index].classList.contains('is-active')) return;
      activeIndex = index;
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    /*
     * Observer fires at multiple thresholds so the callback runs frequently
     * as slides enter and leave the viewport. On each call we find which slide
     * center is nearest to the viewport center and make that dot active.
     * Only one dot is active at a time.
     */
    dotsObserver = new IntersectionObserver(function () {
      var viewportCenter = window.innerHeight * 0.5;
      var bestIndex = 0;
      var bestDistance = Infinity;

      slides.forEach(function (item, index) {
        var rect = item.getBoundingClientRect();
        var isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
        if (isVisible) {
          var itemCenter = rect.top + rect.height / 2;
          var distance = Math.abs(itemCenter - viewportCenter);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        }
      });

      setActive(bestIndex);
      updateDotsVisibility();
    }, {
      root: null,
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
    });

    slides.forEach(function (slide) { dotsObserver.observe(slide); });

    // Also update visibility on scroll (for smooth opacity transitions when
    // the user scrolls past the media area between IO callbacks)
    dotsScrollHandler = function () { updateDotsVisibility(); };
    dotsResizeHandler = function () { updateDotsVisibility(); };
    window.addEventListener('scroll', dotsScrollHandler, { passive: true });
    window.addEventListener('resize', dotsResizeHandler);

    updateDotsVisibility();
  }

  /* ─────────────────────────────────────────────────────────────────────────
     3. STICKY ACCORDION OVERFLOW
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * NOTE: sticky overflow is intentionally NOT managed with JS.
   *
   * CSS position:sticky ends naturally when the sticky element reaches
   * the bottom of its containing block (the product grid row, whose height
   * is driven by the taller image stack column). This means:
   *   • While images are in view, the details panel stays sticky at top:Xpx.
   *   • If open accordions push the panel taller than the viewport, the
   *     bottom content is revealed as the user scrolls toward the last image
   *     and the sticky releases at the end of the product section.
   *   • No internal scrollbar is ever created.
   *
   * A JS-driven "disable-sticky" toggle (the previous approach) caused the
   * panel to lose its sticky position mid-scroll and disappear above the
   * viewport while the user was still browsing product images.
   */
  function initStickyAccordionOverflow() {
    /* intentionally a no-op — see note above */
  }

  /* ─────────────────────────────────────────────────────────────────────────
     4. ADD-TO-BAG FEEDBACK FLOW
     ADD TO BAG → ADDING → ADDED → ADD TO BAG  +  "Item added" message.
  ───────────────────────────────────────────────────────────────────────── */

  /**
   * Duration (ms) that matches the CSS `transition: opacity 0.12s ease` on
   * `.add-to-cart-text__content`. Both must stay in sync.
   */
  var ATC_FADE_MS = 120;

  /**
   * Transitions the ATC button label with a fade-out → text-swap → fade-in
   * sequence, silencing the MutationObserver during the transition so it
   * cannot interrupt mid-fade and snap the text back.
   *
   * @param {'select'|'add'|'adding'|'added'|'soldout'|'waitlist'|'preorder'} newState
   */
  function setAtcState(newState) {
    // Drive non-text state synchronously (form attribute, section attribute)
    currentState = newState;

    var form = getProductForm();
    if (form) {
      form.setAttribute('data-culians-size-state', newState);
    }
    var section = getSection();
    if (section) {
      section.setAttribute(
        'data-culians-product-mode',
        isAllSizesUnavailable() ? 'waitlist' : 'normal'
      );

      // Un-disable the button in waitlist state so the click handler
      // can intercept it and open the drawer. (See setState above.)
      if (newState === 'waitlist') {
        var btn = section.querySelector('.add-to-cart-button');
        if (btn) btn.removeAttribute('disabled');
      }

      // Notify the inline waitlist (or any other listeners) so they can
      // reset their UI when the customer changes to a different state.
      section.dispatchEvent(new CustomEvent('culians:state-change', {
        detail: { state: newState }
      }));
    }

    // Capture a fade token so stale closures can self-abort
    var myFadeId = ++atcFadeId;

    var span = getAtcTextSpan();
    var content = span ? span.closest('.add-to-cart-text__content') : null;

    if (!content) {
      // No fade container found — fall back to instant text set
      applyStateText();
      setTimeout(applyStateText, 100);
      setTimeout(applyStateText, 400);
      return;
    }

    // Phase 1: fade out
    isAtcFading = true;
    content.style.opacity = '0';

    setTimeout(function () {
      if (atcFadeId !== myFadeId) return; // superseded by a newer call

      // Swap text while invisible
      isApplyingText = true;
      var target = getAtcTextSpan();
      if (target) target.textContent = getTextForState(newState);
      isApplyingText = false;

      // Phase 2: fade in
      content.style.opacity = '1';

      setTimeout(function () {
        if (atcFadeId !== myFadeId) return;
        isAtcFading = false;
      }, ATC_FADE_MS);

    }, ATC_FADE_MS);
  }

  function initAddToBagFlow() {
    var section = getSection();
    if (!section) return;

    // Guard: attach only once per section element (section element is replaced
    // on shopify:section:load, so re-attaching to the new element is correct).
    if (section.getAttribute('data-culians-atc-flow')) return;
    section.setAttribute('data-culians-atc-flow', 'true');

    /* ── Override Atelier's handleClick on the add-to-cart-component instance ──
     *
     * WHY THIS IS NECESSARY:
     * component.js registers ALL on:* event handlers at DOCUMENT level with
     * { capture: true } (component.js:registerEventListeners). This means
     * Atelier's handleClick fires in the document-capture phase — before any
     * section-level capture listener can call stopPropagation(). Our previous
     * stopPropagation() approach failed because it ran too late.
     *
     * By overriding handleClick directly on the instance, when Atelier's
     * document-capture dispatch calls `instance.handleClick(event)`, it
     * invokes our no-op instead of the original. This prevents both:
     *   • #animateFlyToCart()  — the black element that flies across the screen
     *   • animateAddToCart()   — sets data-added="true", which hides
     *                            .add-to-cart-text via CSS
     *
     * The button remains type="submit", so user clicks still naturally trigger
     * form submission. We don't need requestSubmit() anymore.
     * ─────────────────────────────────────────────────────────────────────── */
    function suppressAtcAnimation() {
      var comp = section.querySelector('add-to-cart-component');
      if (comp) comp.handleClick = function () {};
    }
    suppressAtcAnimation();

    // Re-apply after variant updates — Atelier may morph the product-form-buttons
    // container which could replace the add-to-cart-component instance.
    section.addEventListener('variant:update', suppressAtcAnimation);

    /* ── Button enable/disable helpers ────────────────────────────── */
    function disableBtn() {
      var comp = section.querySelector('add-to-cart-component');
      if (comp && typeof comp.disable === 'function') comp.disable();
    }

    function enableBtn() {
      var comp = section.querySelector('add-to-cart-component');
      if (comp && typeof comp.enable === 'function') comp.enable();
    }

    /* ── inline message area ─────────────────────────────────────────
     * A single <p class="culians-add-success"> is inserted immediately
     * (below the add-to-cart-component) so the reserved space exists in
     * the DOM from page load. Showing or changing the message only
     * toggles opacity — no DOM insertion, no layout shift.
     * ─────────────────────────────────────────────────────────────── */
    (function reserveInlineMsgSpace() {
      if (section.querySelector('.culians-add-success')) return;
      var comp = section.querySelector('add-to-cart-component');
      if (!comp) return;
      var p = document.createElement('p');
      p.className = 'culians-add-success';
      comp.insertAdjacentElement('afterend', p);
    }());

    function showInlineMsg(text) {
      var msg = section.querySelector('.culians-add-success');
      if (!msg) return;
      msg.textContent = text;
      msg.setAttribute('data-visible', 'true');
    }

    /* The note belongs to a variant: it clears on any size/colour change
     * and on a successful add (Suzan, 2026-09-08). */
    function hideInlineMsg() {
      var msg = section.querySelector('.culians-add-success');
      if (msg) msg.removeAttribute('data-visible');
    }
    section.addEventListener('variant:update', hideInlineMsg);

    /* ── submit capture: set ADDING state, disable button ─────────── */
    section.addEventListener('submit', function (e) {
      var form = e.target;
      // Only intercept product form submissions.
      if (!form || !form.closest('product-form-component')) return;

      // Button is disabled during adding/added — this branch is defensive only.
      if (currentState === 'adding') return;

      // Only intercept orderable states.
      if (currentState !== 'add' && currentState !== 'preorder') return;

      // Detect pre-fetch max-quantity block: Atelier's handleSubmit runs first
      // (document-capture) and disables the button synchronously if the cart
      // already contains the maximum allowed quantity. If the button is now
      // disabled but we haven't entered 'adding' state, Atelier blocked it.
      var atcBtn = section.querySelector('.add-to-cart-button');
      if (atcBtn && atcBtn.disabled) {
        // Suppress Atelier's red error element; show our styled message instead.
        var pfComp = form.closest('product-form-component');
        var atcError = pfComp ? pfComp.querySelector('[ref="addToCartTextError"]') : null;
        if (atcError) atcError.classList.add('hidden');
        showInlineMsg(TEXT_MAX_QTY);
        return;
      }

      stateBeforeAdding = currentState;
      setAtcState('adding');
      disableBtn(); // prevent double-submit while request is in flight

      // Safety fallback: restore state if cart events never arrive (network failure).
      if (addingTimeoutId) clearTimeout(addingTimeoutId);
      addingTimeoutId = setTimeout(function () {
        if (currentState === 'adding') {
          setAtcState(stateBeforeAdding);
          enableBtn();
        }
      }, 15000);
    }, true);

    /* ── cart:update: success OR error-via-update ─────────────────── */
    section.addEventListener('cart:update', function (e) {
      if (currentState !== 'adding') return;
      var data = e.detail && e.detail.data;
      if (!data || data.source !== 'product-form-component') return;

      if (addingTimeoutId) { clearTimeout(addingTimeoutId); addingTimeoutId = null; }

      if (data.didError) {
        // Cart error dispatched as cart:update with didError:true (e.g. quantity exceeded).
        // Atelier's own error element is hidden via CSS; show our styled message instead.
        setAtcState(stateBeforeAdding);
        enableBtn();
        showInlineMsg(TEXT_MAX_QTY);
        return;
      }

      hideInlineMsg();
      // Success. The bag drawer (culians-bag.js) listens for this same
      // cart:update event globally and will open itself with the new item
      // shown. We no longer show the inline "Item added" message —
      // the drawer opening IS the confirmation.
      setAtcState('added');

      setTimeout(function () {
        if (currentState === 'added') {
          setAtcState(stateBeforeAdding);
          enableBtn();
        }
      }, 1500);
    });

    /* ── cart:error: explicit error event ────────────────────────── */
    section.addEventListener('cart:error', function () {
      if (currentState !== 'adding') return;
      if (addingTimeoutId) { clearTimeout(addingTimeoutId); addingTimeoutId = null; }
      setAtcState(stateBeforeAdding);
      enableBtn();
      showInlineMsg(TEXT_MAX_QTY);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     ACCORDION AUTO-SCROLL
     After an accordion opens, scroll the group-block container so the newly
     revealed content is fully in view. Fires only on open, not on close.
  ───────────────────────────────────────────────────────────────────────── */

  function initAccordionAutoScroll() {
    var section = getSection();
    if (!section) return;

    section.addEventListener('click', function (e) {
      var summary = e.target.closest('.details__header');
      if (!summary) return;

      var details = summary.closest('details');
      if (!details) return;

      // details.open reflects the state BEFORE the click toggles it.
      // Only proceed if the accordion is about to open.
      if (details.open) return;

      var detailsContent = details.querySelector('.details-content');
      if (!detailsContent) return;

      var scrollContainer = section.querySelector('[data-testid="group-block"]');
      if (!scrollContainer) return;

      // Wait for the open animation to finish (opacity transition is reliable
      // across browsers and fires at the end of the panel reveal).
      function onTransitionEnd(evt) {
        if (evt.propertyName !== 'opacity') return;
        detailsContent.removeEventListener('transitionend', onTransitionEnd);

        var containerRect = scrollContainer.getBoundingClientRect();
        var contentRect = detailsContent.getBoundingClientRect();

        if (contentRect.bottom > containerRect.bottom) {
          var overshoot = contentRect.bottom - containerRect.bottom;
          scrollContainer.scrollBy({ top: overshoot, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      }

      detailsContent.addEventListener('transitionend', onTransitionEnd);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     INIT & SECTION RE-RENDER HANDLING
  ───────────────────────────────────────────────────────────────────────── */

  function init() {
    // Feature 1: five-state CTA button
    if (hasSizeOption()) {
      // Set initial product mode for CSS
      var section = getSection();
      if (section) {
        section.setAttribute(
          'data-culians-product-mode',
          isAllSizesUnavailable() ? 'waitlist' : 'normal'
        );
      }

      // Determine initial state.
      // When all sizes are unavailable the button must immediately show JOIN WAITLIST.
      // Otherwise always start with SELECT A SIZE — Atelier pre-selects the first
      // variant in the DOM but the user hasn't made an explicit choice yet, so we
      // don't count that as "a size is selected".
      var initialState = isAllSizesUnavailable() ? 'waitlist' : 'select';

      setState(initialState);
      attachSizeListeners();
      attachSelectClickFlash();
      watchAtcText();
      attachVariantUpdateListener();

      // Pre-fetch variant data for accurate pre-order / sold-out detection.
      // Only re-evaluate state if the user has already selected a size;
      // otherwise the cache warm-up would override SELECT A SIZE with ADD TO BAG.
      fetchVariantData(function () {
        if (userHasSelectedSize) setState(determineCTAState());
      });
    }

    // Fix "Color : Navy" label spacing (trim trailing space from text nodes)
    fixColorLabel();

    // Feature 2: pagination dots
    initPaginationDots();

    // Feature 3: sticky accordion overflow management
    initStickyAccordionOverflow();

    // Feature 4: add-to-bag feedback flow
    initAddToBagFlow();

    // Feature 5: auto-scroll on accordion open
    initAccordionAutoScroll();

    // Feature 6: highlights paragraph above the DETAILS accordion
    initHighlights();

    // Feature 7: size guide drawer
    initSizeGuide();

    // Feature 8: waitlist — initialized in the mode set by WAITLIST_MODE
    // (defined below near KLAVIYO_COMPANY_ID). Both implementations
    // (initWaitlistInline + initWaitlistDrawer) stay in this file; only
    // one is wired up at a time. Flip WAITLIST_MODE to switch.
    initWaitlist();
  }

  /* ─────────────────────────────────────────────────────────────────────────
     6. HIGHLIGHTS PARAGRAPH (reference pattern)

     Reads product.metafields.custom.highlights from the data attribute on
     the .product-information section (set in product-information-content.liquid)
     and injects a <p class="culians-highlights"> directly above the accordion.

     Per-product editable in Shopify Admin → Product → Metafields → Highlights.
     If the metafield is empty for a given product, nothing is rendered.
  ───────────────────────────────────────────────────────────────────────── */

  function initHighlights() {
    var section = getSection();
    if (!section) return;

    // Idempotent — don't double-inject on re-render.
    if (section.querySelector('.culians-highlights')) return;

    var highlightsText = (section.getAttribute('data-culians-highlights') || '').trim();
    if (!highlightsText) return;

    // Find the accordion to insert above.
    var accordion = section.querySelector('.accordion--dividers') ||
                    section.querySelector('.accordion');
    if (!accordion) return;

    var p = document.createElement('p');
    p.className = 'culians-highlights';
    p.textContent = highlightsText;

    accordion.parentNode.insertBefore(p, accordion);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     7. SIZE GUIDE DRAWER

     Slide-in drawer triggered by any "Size Guide" link on the product page.
     The drawer markup is rendered by snippets/culians-size-guide-drawer.liquid.

     Behavior:
       - Click on any link whose href contains "size-guide" → open drawer
       - Close via: × button, backdrop click, or ESC key
       - When open, body scroll is locked
       - cm/in unit toggle switches which measurement cells are visible
  ───────────────────────────────────────────────────────────────────────── */

  function initSizeGuide() {
    var drawer = document.getElementById('culians-size-guide');
    if (!drawer) return;

    // Idempotent — don't double-attach on re-render
    if (drawer.getAttribute('data-culians-sg-init') === 'true') return;
    drawer.setAttribute('data-culians-sg-init', 'true');

    function openDrawer() {
      drawer.setAttribute('data-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('culians-size-guide-open');
    }

    function closeDrawer() {
      drawer.setAttribute('data-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('culians-size-guide-open');
    }

    // Intercept clicks on any Size Guide link inside the product section.
    // We catch this at the document level so it works regardless of where
    // the link is rendered (group block, header injection, etc.).
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="size-guide"]');
      if (!link) return;
      // Only intercept inside a product context
      if (!link.closest('[data-testid="product-information"]')) return;
      e.preventDefault();
      openDrawer();
    });

    // Close handlers
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-culians-sg-close]')) {
        e.preventDefault();
        closeDrawer();
      }
    });

    // ESC key closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') {
        closeDrawer();
      }
    });

    // cm / in unit toggle
    drawer.addEventListener('click', function (e) {
      var unitBtn = e.target.closest('[data-culians-sg-unit]');
      if (!unitBtn) return;
      var unit = unitBtn.getAttribute('data-culians-sg-unit');
      drawer.setAttribute('data-unit', unit);

      // Update active state on buttons
      var btns = drawer.querySelectorAll('[data-culians-sg-unit]');
      Array.prototype.forEach.call(btns, function (b) {
        var isActive = b.getAttribute('data-culians-sg-unit') === unit;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     8. WAITLIST DRAWER

     Slide-in drawer triggered when the customer clicks JOIN WAITLIST on a
     sold-out variant. Markup rendered by snippets/culians-waitlist-drawer.liquid.

     Behavior:
       - Intercepts clicks on the .add-to-cart-button when currentState ===
         'waitlist' or 'soldout' (prevents form submit; opens drawer instead)
       - Populates drawer with current variant context (color, size, variant ID)
       - Email form submits to Klaviyo's back-in-stock-subscriptions endpoint
       - On success: shows confirmation, drawer auto-closes after 4s
       - Close via: Close link, backdrop click, ESC key, Continue browsing
       - Body scroll locked while open

     Klaviyo configuration:
       Set KLAVIYO_COMPANY_ID below to your Klaviyo Public API Key (also
       called Company ID — public, safe to include client-side).
       Find it in Klaviyo: Account → Settings → API Keys → Public API Key.
       Leaving it empty disables the actual API call; the drawer will still
       show success UI for testing.
  ───────────────────────────────────────────────────────────────────────── */

  // Klaviyo Public API Key (Company ID) — public, safe to include client-side.
  var KLAVIYO_COMPANY_ID = 'WCDkCL';

  /* Waitlist UX mode — controls which JOIN WAITLIST experience is wired up.
   *   'inline' → email form replaces the CTA button in place (reference pattern)
   *   'drawer' → slide-in drawer from the right (same idiom as Size Guide + Bag)
   * Flip this single value to swap experiences. Both implementations stay
   * defined in this file (initWaitlistInline and initWaitlistDrawer below);
   * only the one matching WAITLIST_MODE is initialized. */
  var WAITLIST_MODE = 'drawer'; // 'inline' | 'drawer'

  function initWaitlist() {
    if (WAITLIST_MODE === 'drawer') {
      initWaitlistDrawer();
    } else {
      initWaitlistInline();
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     INLINE WAITLIST (reference pattern)

     Three states in the PDP CTA slot:
       idle    – JOIN WAITLIST button (Atelier's CTA, our setState drives label)
       form    – Replaces button: email input + submit
       success – Replaces form: "Thank you" message

     The inline form element is injected once on init via JS. Each state is
     shown / hidden via the [data-state] attribute on the inline root (CSS in
     culians-waitlist-inline.css). When the form or success state is active,
     CSS hides the original .add-to-cart-button so the inline form fills the
     slot.
  ───────────────────────────────────────────────────────────────────────── */

  function initWaitlistInline() {
    var section = getSection();
    if (!section) return;

    // Guard: only init once per section instance.
    if (section.getAttribute('data-culians-wl-inline-init') === 'true') return;
    section.setAttribute('data-culians-wl-inline-init', 'true');

    var atcButton = section.querySelector('.add-to-cart-button');
    if (!atcButton) return;

    /* ── Build inline form DOM ─────────────────────────────── */
    var inline = document.createElement('div');
    inline.className = 'culians-waitlist-inline';
    inline.setAttribute('data-state', 'idle');
    inline.innerHTML = [
      '<div class="culians-waitlist-inline__form-state">',
      '  <p class="culians-waitlist-inline__label">Join the waitlist</p>',
      '  <form class="culians-waitlist-inline__form" novalidate>',
      '    <input',
      '      type="email"',
      '      name="email"',
      '      placeholder="Email"',
      '      required',
      '      autocomplete="email"',
      '      class="culians-waitlist-inline__input"',
      '    >',
      '    <p class="culians-waitlist-inline__error" hidden></p>',
      '    <button type="submit" class="culians-waitlist-inline__submit">JOIN WAITLIST</button>',
      '  </form>',
      '</div>',
      '<div class="culians-waitlist-inline__success-state">',
      '  <p class="culians-waitlist-inline__label">Join the waitlist</p>',
      '  <p class="culians-waitlist-inline__thank-you">Thank you. You will receive an email when this item is back in stock.</p>',
      '</div>'
    ].join('\n');

    // Insert right after the ATC button so they share the slot.
    atcButton.insertAdjacentElement('afterend', inline);

    var formEl = inline.querySelector('.culians-waitlist-inline__form');
    var emailInput = inline.querySelector('.culians-waitlist-inline__input');
    var submitBtn = inline.querySelector('.culians-waitlist-inline__submit');
    var errorEl = inline.querySelector('.culians-waitlist-inline__error');

    /* ── State helpers ─────────────────────────────────────── */
    function setInlineState(state) {
      inline.setAttribute('data-state', state);
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.removeAttribute('hidden');
      emailInput.setAttribute('aria-invalid', 'true');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.setAttribute('hidden', '');
      errorEl.textContent = '';
      emailInput.removeAttribute('aria-invalid');
    }

    function showSuccess() {
      submitBtn.removeAttribute('disabled');
      submitBtn.textContent = 'JOIN WAITLIST';
      setInlineState('success');
      // After 4 seconds, revert to idle so the page returns to its
      // resting state (CTA button reappears if state is still waitlist,
      // or normal ADD TO BAG if customer has since picked an in-stock
      // size). 4s is enough time to read the confirmation without
      // dwelling.
      setTimeout(function () {
        // Only auto-revert if we're still on success — don't override
        // a manual state change the customer made in the meantime.
        if (inline.getAttribute('data-state') === 'success') {
          setInlineState('idle');
          // Clear the email so the form is fresh next time.
          emailInput.value = '';
        }
      }, 4000);
    }

    /* ── Klaviyo Back-in-Stock submission ──────────────────── */
    function submitToKlaviyo(email, variantId) {
      if (!KLAVIYO_COMPANY_ID) {
        console.warn('Culians waitlist: KLAVIYO_COMPANY_ID not set.');
        showSuccess();
        return;
      }

      submitBtn.setAttribute('disabled', '');
      submitBtn.textContent = 'SUBMITTING…';

      var url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
                encodeURIComponent(KLAVIYO_COMPANY_ID);

      var body = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId }
            }
          }
        }
      };

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          if (response.status === 202 || response.ok) {
            showSuccess();
          } else {
            return response.json().then(function (data) {
              var msg = (data && data.errors && data.errors[0] && data.errors[0].detail) ||
                        'Something went wrong. Please try again.';
              submitBtn.removeAttribute('disabled');
              submitBtn.textContent = 'JOIN WAITLIST';
              showError(msg);
            }).catch(function () {
              submitBtn.removeAttribute('disabled');
              submitBtn.textContent = 'JOIN WAITLIST';
              showError('Something went wrong. Please try again.');
            });
          }
        })
        .catch(function () {
          submitBtn.removeAttribute('disabled');
          submitBtn.textContent = 'JOIN WAITLIST';
          showError('Network error. Please check your connection and try again.');
        });
    }

    /* ── Live email validation: clear error as soon as the typed
     *    email becomes valid. The customer shouldn't have to wait
     *    until they re-submit to see they corrected the issue.
     *    Wired to multiple events (input, change, keyup, paste, blur)
     *    so it fires regardless of how the value updates — direct
     *    typing, paste, autofill, IME composition end, etc. */
    function liveValidateEmail() {
      var email = (emailInput.value || '').trim();
      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        clearError();
      }
    }
    emailInput.addEventListener('input', liveValidateEmail);
    emailInput.addEventListener('change', liveValidateEmail);
    emailInput.addEventListener('keyup', liveValidateEmail);
    emailInput.addEventListener('blur', liveValidateEmail);
    // Paste fires before the value is updated; defer to the next tick.
    emailInput.addEventListener('paste', function () {
      setTimeout(liveValidateEmail, 0);
    });

    /* ── Click on JOIN WAITLIST CTA → expand inline form ───── */
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('.add-to-cart-button');
      if (!btn) return;
      if (currentState !== 'waitlist') return;
      e.preventDefault();
      e.stopPropagation();
      setInlineState('form');
      // Focus the email field so the keyboard / cursor is ready.
      setTimeout(function () { emailInput.focus(); }, 50);
    }, true);

    /* ── Submit form → Klaviyo ─────────────────────────────── */
    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      clearError();

      var email = (emailInput.value || '').trim();
      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        showError('Please enter a valid email address.');
        return;
      }

      var variantId = getSelectedVariantId();
      if (!variantId) {
        showError('Please select a size before joining the waitlist.');
        return;
      }

      submitToKlaviyo(email, variantId);
    });

    /* ── Reset to idle when variant changes back to in-stock ─
     * If the customer picks a different (available) size after the form
     * is open, we should collapse back to the idle CTA. The size-change
     * handler already calls setState — we listen for state transitions
     * away from 'waitlist' and reset the inline form. */
    section.addEventListener('culians:state-change', function (e) {
      var newState = e.detail && e.detail.state;
      if (newState && newState !== 'waitlist') {
        setInlineState('idle');
      }
    });
  }

  function initWaitlistDrawer() {
    var drawer = document.getElementById('culians-waitlist');
    if (!drawer) return;

    // Idempotent — don't double-attach on re-render
    if (drawer.getAttribute('data-culians-wl-init') === 'true') return;
    drawer.setAttribute('data-culians-wl-init', 'true');

    var formView = drawer.querySelector('[data-culians-wl-view="form"]');
    var successView = drawer.querySelector('[data-culians-wl-view="success"]');
    var form = drawer.querySelector('[data-culians-wl-form]');
    var emailInput = form.querySelector('input[name="email"]');
    var variantIdInput = drawer.querySelector('[data-culians-wl-variant-id]');
    var submitBtn = drawer.querySelector('[data-culians-wl-submit]');
    var errorEl = drawer.querySelector('[data-culians-wl-error]');
    var colorEl = drawer.querySelector('[data-culians-wl-color]');
    var sizeEl = drawer.querySelector('[data-culians-wl-size]');
    var successMessageEl = drawer.querySelector('[data-culians-wl-success-message]');
    var autoCloseTimer = null;

    function getSelectedColor() {
      var section = getSection();
      if (!section) return '';
      // Find the swatch label that contains the checked input
      var checked = section.querySelector(
        '.variant-option--swatches input[type="radio"]:checked'
      );
      if (!checked) return '';
      var label = checked.closest('.variant-option__button-label');
      if (!label) return '';
      // The swatch label text is in a child span; fallback to label text
      var textSpan = label.querySelector('.variant-option__button-label__text');
      return (textSpan ? textSpan.textContent : label.textContent).trim();
    }

    function getSelectedSize() {
      var section = getSection();
      if (!section) return '';
      var checked = section.querySelector(
        '.variant-option--buttons:not(.variant-option--swatches) input[type="radio"]:checked'
      );
      if (!checked) return '';
      var label = checked.closest('.variant-option__button-label');
      if (!label) return '';
      var textSpan = label.querySelector('.variant-option__button-label__text');
      return (textSpan ? textSpan.textContent : label.textContent).trim();
    }

    function populateDrawer() {
      var color = getSelectedColor();
      var size = getSelectedSize();
      var variantId = getSelectedVariantId();

      colorEl.textContent = color || '';
      sizeEl.textContent = size ? 'Size ' + size : '';
      variantIdInput.value = variantId || '';
    }

    function showForm() {
      formView.removeAttribute('hidden');
      successView.setAttribute('hidden', '');
      errorEl.textContent = '';
      emailInput.setAttribute('aria-invalid', 'false');
      submitBtn.removeAttribute('disabled');
      submitBtn.textContent = 'NOTIFY ME';
    }

    /* Registrations this browser has already made (variant + email), so a
     * repeat sign-up for the same size can say so instead of "Thank you".
     * Klaviyo de-duplicates server-side either way; this is wording only.
     * Another device or a cleared browser simply sees the normal thank-you
     * (2026-09-13). */
    var WL_STORE = 'culians_waitlist_registered';
    function wlKey(email, variantId) { return String(variantId) + '|' + String(email || '').trim().toLowerCase(); }
    function wlRegistered(email, variantId) {
      try { return (JSON.parse(localStorage.getItem(WL_STORE) || '[]') || []).indexOf(wlKey(email, variantId)) !== -1; } catch (e) { return false; }
    }
    function wlRemember(email, variantId) {
      try {
        var list = JSON.parse(localStorage.getItem(WL_STORE) || '[]') || [];
        var k = wlKey(email, variantId);
        if (list.indexOf(k) === -1) list.push(k);
        localStorage.setItem(WL_STORE, JSON.stringify(list.slice(-50)));
      } catch (e) {}
    }

    function showSuccess(alreadyRegistered) {
      var productTitle = drawer.getAttribute('data-product-title') || 'this item';
      var size = sizeEl.textContent || '';
      var titleEl = drawer.querySelector('[data-culians-wl-success-title]');
      if (titleEl) titleEl.textContent = alreadyRegistered ? "You're already on the list for this size." : 'Thank you.';
      var message = size
        ? "We'll let you know when " + productTitle + ', ' + size + ' is back.'
        : "We'll let you know when " + productTitle + ' is back.';

      successMessageEl.textContent = message;
      formView.setAttribute('hidden', '');
      successView.removeAttribute('hidden');

      // Auto-close after 4 seconds
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
      autoCloseTimer = setTimeout(function () {
        closeDrawer();
      }, 4000);
    }

    function showError(message) {
      errorEl.textContent = message;
      emailInput.setAttribute('aria-invalid', 'true');
      submitBtn.removeAttribute('disabled');
      submitBtn.textContent = 'NOTIFY ME';
    }

    /* Track the element that had focus before the drawer opened so we
     * can return focus to it on close. Critical for screen-reader and
     * keyboard users — without this, focus jumps to the top of the
     * document on close. */
    var lastFocusedElement = null;

    function openDrawer() {
      lastFocusedElement = document.activeElement;
      populateDrawer();
      showForm();
      form.reset();
      drawer.setAttribute('data-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('culians-waitlist-open');
      // Auto-focus the email field after the slide-in begins so the
      // keyboard is ready and screen readers land in the form.
      setTimeout(function () { emailInput.focus(); }, 100);
    }

    function closeDrawer() {
      if (autoCloseTimer) { clearTimeout(autoCloseTimer); autoCloseTimer = null; }
      drawer.setAttribute('data-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('culians-waitlist-open');
      // Reset to form view after animation completes, and restore
      // focus to whatever opened the drawer (usually JOIN WAITLIST).
      setTimeout(function () {
        showForm();
        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
          lastFocusedElement.focus();
          lastFocusedElement = null;
        }
      }, 300);
    }

    /* Focus trap — when the drawer is open, Tab key cycling stays
     * inside the drawer. Always-preventDefault version: we compute
     * the next focusable element manually and focus it ourselves,
     * regardless of position in the cycle. The listener lives at the
     * document level so other elements can't intercept Tab first.
     * Visible-only filter — the success-section's Continue browsing
     * button is in the DOM but [hidden] when in form state, so it
     * must be excluded. */
    function drawerFocusables() {
      var all = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return Array.prototype.filter.call(all, function (el) {
        return el.offsetParent !== null && !el.closest('[hidden]');
      });
    }
    // Anything that gains focus outside the open drawer (a Tab the trap
    // missed, a click on the backdrop page) is pulled back to the first
    // field (2026-09-13).
    document.addEventListener('focusin', function (e) {
      if (drawer.getAttribute('data-open') !== 'true') return;
      if (drawer.contains(e.target)) return;
      var f = drawerFocusables();
      if (f.length) f[0].focus();
    });
    // Capture phase: runs before Atelier's own key handlers, so nothing
    // upstream can stop the Tab from reaching the trap (2026-09-13).
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (drawer.getAttribute('data-open') !== 'true') return;
      var all = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      var focusable = Array.prototype.filter.call(all, function (el) {
        // offsetParent === null means display:none (or inside a [hidden] section).
        // Also explicitly skip any ancestor with the hidden attribute.
        return el.offsetParent !== null && !el.closest('[hidden]');
      });
      if (focusable.length === 0) return;
      var currentIndex = Array.prototype.indexOf.call(focusable, document.activeElement);
      var nextIndex;
      if (currentIndex === -1) {
        // Focus is outside the drawer's focusable list — pull it back in.
        nextIndex = 0;
      } else if (e.shiftKey) {
        nextIndex = currentIndex === 0 ? focusable.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex === focusable.length - 1 ? 0 : currentIndex + 1;
      }
      e.preventDefault();
      e.stopPropagation();
      focusable[nextIndex].focus();
    }, true);

    /* ── Submit to Klaviyo Back-in-Stock subscriptions API ───────
     * Endpoint: https://a.klaviyo.com/client/back-in-stock-subscriptions/
     * Requires: public Company ID, variant ID, email
     * Catalog variant ID format: $shopify:::$default:::<variant_id>
     * Docs: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
     */
    function submitWaitlist(email, variantId) {
      // If no Klaviyo company ID configured, skip the API call but still
      // show the success state (for UI testing without backend wired up).
      if (!KLAVIYO_COMPANY_ID) {
        console.warn(
          'Culians waitlist: KLAVIYO_COMPANY_ID not set in culians-product-page.js. ' +
          'Email will NOT be saved. Set the constant to enable Klaviyo integration.'
        );
        showSuccess();
        return;
      }

      var already = wlRegistered(email, variantId);

      submitBtn.setAttribute('disabled', '');
      submitBtn.textContent = 'SUBMITTING…';

      var url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
                encodeURIComponent(KLAVIYO_COMPANY_ID);

      var body = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + variantId
              }
            }
          }
        }
      };

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          if (response.status === 202 || response.ok) {
            wlRemember(email, variantId);
            showSuccess(already);
          } else {
            return response.json().then(function (data) {
              var msg = (data && data.errors && data.errors[0] && data.errors[0].detail) ||
                        'Something went wrong. Please try again.';
              showError(msg);
            }).catch(function () {
              showError('Something went wrong. Please try again.');
            });
          }
        })
        .catch(function () {
          showError('Network error. Please check your connection and try again.');
        });
    }

    // ── Click on JOIN WAITLIST CTA → open drawer ─────────────
    var section = getSection();
    if (section) {
      section.addEventListener('click', function (e) {
        var btn = e.target.closest('.add-to-cart-button');
        if (!btn) return;
        // Only intercept when the current state is waitlist
        if (currentState !== 'waitlist') return;
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
      }, true); // capture phase so we intercept before Atelier's submit handler
    }

    // ── Close handlers ───────────────────────────────────────
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-culians-wl-close]')) {
        e.preventDefault();
        closeDrawer();
      }
    });

    // ESC key closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') {
        closeDrawer();
      }
    });

    /* ── Live email validation: clear the error message as soon as
     *    the typed email becomes valid, without waiting for the next
     *    submit. Wired to multiple events so it fires regardless of
     *    how the value updates — direct typing, paste, autofill, IME
     *    composition end, etc. Mirrors the inline waitlist behavior. */
    function liveValidateEmail() {
      var email = (emailInput.value || '').trim();
      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        errorEl.textContent = '';
        emailInput.setAttribute('aria-invalid', 'false');
      }
    }
    emailInput.addEventListener('input', liveValidateEmail);
    emailInput.addEventListener('change', liveValidateEmail);
    emailInput.addEventListener('keyup', liveValidateEmail);
    emailInput.addEventListener('blur', liveValidateEmail);
    // Paste fires before the value is updated; defer to the next tick.
    emailInput.addEventListener('paste', function () {
      setTimeout(liveValidateEmail, 0);
    });

    // ── Form submit ──────────────────────────────────────────
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (emailInput.value || '').trim();
      var variantId = variantIdInput.value || getSelectedVariantId();

      // Validate email format
      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        showError('Please enter a valid email address.');
        return;
      }
      if (!variantId) {
        showError('Please select a size before joining the waitlist.');
        return;
      }

      // Reset error state
      errorEl.textContent = '';
      emailInput.setAttribute('aria-invalid', 'false');

      // Newsletter checkbox removed 2026-09-11 (A5): the back-in-stock
      // request is a single-purpose moment; Subscribe lives in the footer
      // and on its own page, behind Shopify's double opt-in.

      submitWaitlist(email, variantId);
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-apply after Shopify re-renders the section (variant change, theme editor, etc.)
  document.addEventListener('shopify:section:load', function () {
    // Reset guard state on re-render
    currentState = 'select';
    isApplyingText = false;
    variantCache = {};
    userHasSelectedSize = false;
    stateBeforeAdding = 'add';
    if (addingTimeoutId) { clearTimeout(addingTimeoutId); addingTimeoutId = null; }
    ++atcFadeId;
    isAtcFading = false;

    requestAnimationFrame(function () {
      if (hasSizeOption()) {
        var section = getSection();
        if (section) {
          section.setAttribute(
            'data-culians-product-mode',
            isAllSizesUnavailable() ? 'waitlist' : 'normal'
          );
        }
        setState('select');
        attachSizeListeners();
        attachSelectClickFlash();
        watchAtcText();
        fetchVariantData(function () {
          if (userHasSelectedSize) setState(determineCTAState());
        });
      }
      fixColorLabel();
      initPaginationDots();
      initStickyAccordionOverflow();
      initAddToBagFlow();
      initAccordionAutoScroll();
      initHighlights();
      initSizeGuide();
      initWaitlist();
    });
  });

})();

/* ── Phone gallery: finite carousel ───────────────────────────
 * Atelier renders the phone slideshow `infinite`, so its prev/next buttons
 * never disable. Culians shows an arrow only where an image exists in that
 * direction (2026-09-09): drop the attribute and re-run the current setter so
 * the disabled state is computed for slide 0. Swipes and arrow taps keep the
 * state in sync through Atelier's own setter afterwards. */
(function () {
  function syncArrows(sc) {
    var refs = sc.refs || {};
    var prev = refs.previous || sc.querySelector('.slideshow-control--previous');
    var next = refs.next || sc.querySelector('.slideshow-control--next');
    var slides = refs.slides || sc.querySelectorAll('slideshow-slide');
    var i = typeof sc.current === 'number' ? sc.current : 0;
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= slides.length - 1;
  }
  /* A fresh product page opens on its first image (2026-09-11). Safari was
   * carrying the previous page's gallery scroll offset into the next
   * product opened from View more (3rd image there = 3rd image here). The
   * reset runs on load and again once images have sized (slides are
   * height:auto), and stops the moment the reader touches the gallery. A
   * bfcache return (pageshow.persisted) keeps the reader's place. */
  function startAtFirst(sc) {
    var touched = false;
    sc.addEventListener('pointerdown', function () { touched = true; }, { once: true, passive: true });
    var target = typeof sc.initialSlideIndex === 'number' ? sc.initialSlideIndex : 0;
    function reset() {
      if (touched) return;
      var scroller = null;
      Array.prototype.some.call(sc.querySelectorAll('slideshow-container, slideshow-slides'), function (el) {
        if (el.scrollWidth > el.clientWidth) { scroller = el; return true; }
        return false;
      });
      try { sc.select(target, undefined, { animate: false }); } catch (e) {}
      if (scroller && target === 0 && scroller.scrollLeft !== 0) scroller.scrollLeft = 0;
      syncArrows(sc);
    }
    reset();
    requestAnimationFrame(function () { requestAnimationFrame(reset); });
    if (document.readyState !== 'complete') window.addEventListener('load', function () { setTimeout(reset, 0); }, { once: true });
    window.addEventListener('pageshow', function (e) { if (!e.persisted) reset(); });
  }
  function finite() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    var sc = document.querySelector('main[data-template^="product"] media-gallery slideshow-component');
    if (!sc || sc.hasAttribute('data-culians-finite')) return;
    sc.setAttribute('data-culians-finite', '');
    sc.removeAttribute('infinite');
    syncArrows(sc);
    startAtFirst(sc);
    // Atelier updates `disabled` in its own current-setter after every
    // swipe/tap; this covers the first paint and any scroll it misses.
    sc.addEventListener('slideshow:change', function () { syncArrows(sc); });
    var cont = sc.querySelector('slideshow-container');
    if (cont) cont.addEventListener('scrollend', function () { setTimeout(function () { syncArrows(sc); }, 0); });
  }
  function start() {
    var run = function () { finite(); };
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('slideshow-component').then(run, run);
    } else { run(); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('variant:update', function () { setTimeout(start, 50); });
  new MutationObserver(function () { start(); }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ── PHONE ACCORDION REVEAL (2026-09-09) ──────────────────────
 * On phones an opened drawer low on the screen expanded below the fold and
 * the page stayed put, so the reader saw the header flip and nothing else.
 * Reveal rule: after a drawer opens, if its content does not fit in the
 * viewport, scroll so the drawer's title sits just under the sticky header.
 * Nothing moves when the content is already fully visible. Multi-open is
 * kept as on desktop. */
(function () {
  'use strict';
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  var HEADER = 60;
  var GAP = 16;
  function headerHeight() {
    var v = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
    return isNaN(v) ? HEADER : v;
  }
  function reveal(details) {
    var summary = details.querySelector('summary');
    if (!summary) return;
    var r = details.getBoundingClientRect();
    var top = headerHeight() + GAP;
    // The content's height animates open; measure its final size, not the
    // mid-animation frame.
    var content = details.querySelector('.details-content');
    var finalBottom = content ? r.top + summary.getBoundingClientRect().height + content.scrollHeight : r.bottom;
    if (r.top >= top && finalBottom <= window.innerHeight) return; // already in view
    window.scrollTo({ top: window.scrollY + r.top - top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    if (!d.closest('main[data-template^="product"] .accordion, main[data-template^="product"] accordion-custom')) return;
    requestAnimationFrame(function () { reveal(d); });
  }, true);
})();
